import React from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import { PromotionContestV2 } from '../types/ContestTypes';
import { NumberField } from '../../../../../ui/_fields/NumberField/NumberField';
import { TextField } from '../../../../../ui/_fields/TextField/TextField';
import { TextareaFieldInput } from '../../../../../ui/_fields/TextareaFieldInput/TextareaFieldInput';
import { ContestRewardType } from '../contestShared';
import {
  CONTEST_V2_MAX,
  createEmptyContestV2Branch,
  createEmptyContestV2Episode,
} from '../contestV2Types';
import { EpisodeBookValidationMessage } from './EpisodeBookValidationMessage';
import { EpisodeUuidLookupHint } from './EpisodeUuidLookupHint';
import classes from './ContestV2Panel.module.scss';

function getAt(obj: unknown, path: string): unknown {
  if (!path) return obj;
  let cur: unknown = obj;
  for (const seg of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    const key = /^\d+$/.test(seg) ? Number(seg) : seg;
    cur = (cur as Record<string | number, unknown>)[key];
  }
  return cur;
}

function getNestedString(obj: unknown, path: string): string {
  const v = getAt(obj, path);
  return typeof v === 'string' ? v : '';
}

function getNestedTouched(obj: unknown, path: string): boolean {
  return Boolean(getAt(obj, path));
}

interface ContestV2PanelProps {
  values: PromotionContestV2;
  errors: unknown;
  touched: unknown;
  promotionStoryUuid: string | null;
  onChange: (event: React.ChangeEvent) => void;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
}

export function ContestV2Panel(props: ContestV2PanelProps) {
  const {
    values,
    errors,
    touched,
    promotionStoryUuid,
    onChange,
    setFieldValue,
  } = props;

  const episodes = values.episodes ?? [];

  const addEpisode = () => {
    setFieldValue('v2.episodes', [...episodes, createEmptyContestV2Episode()]);
  };

  const removeEpisode = (index: number) => {
    const next = episodes.filter((_, i) => i !== index);
    setFieldValue('v2.episodes', next);
  };

  const addBranch = (episodeIndex: number) => {
    const ep = episodes[episodeIndex];
    if (!ep) return;
    setFieldValue(
      `v2.episodes.${episodeIndex}.branches`,
      [...(ep.branches ?? []), createEmptyContestV2Branch()],
    );
  };

  const removeBranch = (episodeIndex: number, branchIndex: number) => {
    const ep = episodes[episodeIndex];
    if (!ep) return;
    const nextBranches = (ep.branches ?? []).filter((_, i) => i !== branchIndex);
    setFieldValue(`v2.episodes.${episodeIndex}.branches`, nextBranches);
  };

  const updateBranch = (
    episodeIndex: number,
    branchIndex: number,
    patch: { branchName?: string; stepIndex?: number },
  ) => {
    const ep = episodes[episodeIndex];
    if (!ep) return;
    const nextBranches = (ep.branches ?? []).map((b, i) => (i === branchIndex ? { ...b, ...patch } : b));
    setFieldValue(`v2.episodes.${episodeIndex}.branches`, nextBranches);
  };

  const handleRewardAmountChange = (event: React.ChangeEvent) => {
    const { value } = event.target as HTMLInputElement;
    setFieldValue('v2.rewardAmount', value === '' ? 0 : Number(value));
  };

  return (
    <>
      {episodes.map((ep, episodeIndex) => (
        <Card key={ep.id} className="mb-3 border-secondary">
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 py-2 bg-light">
            <span className="font-weight-bold text-dark">#{episodeIndex + 1}</span>
            <Button
              type="button"
              variant={null as any}
              className="text-white border-0 py-0 px-2"
              style={{ backgroundColor: '#888888' }}
              onClick={() => removeEpisode(episodeIndex)}
            >
              Remove episode
            </Button>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={12}>
                <TextField
                  label="Episode UUID"
                  name={`v2.episodes.${episodeIndex}.episodeUuid`}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={ep.episodeUuid}
                  onChange={onChange}
                  error={getNestedTouched(touched, `episodes.${episodeIndex}.episodeUuid`)
                    ? getNestedString(errors, `episodes.${episodeIndex}.episodeUuid`)
                    : ''}
                />
                <EpisodeUuidLookupHint uuid={ep.episodeUuid} />
                <EpisodeBookValidationMessage
                  storyUuid={promotionStoryUuid}
                  episodeUuid={ep.episodeUuid}
                />
              </Col>
            </Row>
            <div className="text-muted small mb-2">
              Branches (each branch name must be unique within this episode)
            </div>
            <Table bordered size="sm" className={`mb-2 ${classes.branchesTable}`}>
              <thead>
                <tr>
                  <th>Branch name</th>
                  <th style={{ width: '140px' }}>Step index</th>
                  <th style={{ width: '56px' }} aria-label="Remove row" />
                </tr>
              </thead>
              <tbody>
                {(ep.branches ?? []).map((br, branchIndex) => {
                  const branchNamePath = `episodes.${episodeIndex}.branches.${branchIndex}.branchName`;
                  const stepIndexPath = `episodes.${episodeIndex}.branches.${branchIndex}.stepIndex`;
                  const branchNameErr = getNestedTouched(touched, branchNamePath)
                    ? getNestedString(errors, branchNamePath)
                    : '';
                  const stepIndexErr = getNestedTouched(touched, stepIndexPath)
                    ? getNestedString(errors, stepIndexPath)
                    : '';
                  return (
                    <tr key={br.id}>
                      <td className="align-middle">
                        <Form.Control
                          type="text"
                          size="sm"
                          name={`v2.episodes.${episodeIndex}.branches.${branchIndex}.branchName`}
                          placeholder="Branch name"
                          value={br.branchName}
                          onChange={onChange}
                          isInvalid={Boolean(branchNameErr)}
                          maxLength={CONTEST_V2_MAX.branchName}
                          aria-label="Branch name"
                        />
                        {branchNameErr ? (
                          <div className="text-danger small mt-1">{branchNameErr}</div>
                        ) : null}
                      </td>
                      <td className="align-middle">
                        <Form.Control
                          type="number"
                          size="sm"
                          name={`v2.episodes.${episodeIndex}.branches.${branchIndex}.stepIndex`}
                          min={0}
                          step={1}
                          value={br.stepIndex}
                          onChange={(e) => {
                            const v = (e.target as HTMLInputElement).value;
                            updateBranch(episodeIndex, branchIndex, {
                              stepIndex: v === '' ? 0 : Number(v),
                            });
                          }}
                          isInvalid={Boolean(stepIndexErr)}
                          aria-label="Step index"
                        />
                        {stepIndexErr ? (
                          <div className="text-danger small mt-1">{stepIndexErr}</div>
                        ) : null}
                      </td>
                      <td className="align-middle text-center">
                        <Button
                          type="button"
                          variant="link"
                          className="text-danger p-0"
                          onClick={() => removeBranch(episodeIndex, branchIndex)}
                          disabled={(ep.branches ?? []).length <= 1}
                          title="Remove branch row"
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Button type="button" variant="secondary" size="sm" onClick={() => addBranch(episodeIndex)}>
              Add branch
            </Button>
          </Card.Body>
        </Card>
      ))}

      <Row className="mb-3">
        <Col>
          <Button type="button" onClick={addEpisode}>
            Add episode
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Reward type</Form.Label>
            <Form.Control
              as="select"
              name="v2.rewardType"
              value={values.rewardType}
              onChange={onChange}
            >
              <option value={ContestRewardType.Sapphires}>Sapphires</option>
              <option value={ContestRewardType.Hearts}>Hearts</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <NumberField
            label="Reward amount"
            name="v2.rewardAmount"
            min="0"
            step="1"
            value={values.rewardAmount}
            onChange={handleRewardAmountChange}
            error={getNestedTouched(touched, 'rewardAmount') ? getNestedString(errors, 'rewardAmount') : ''}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <TextField
            label="Title"
            name="v2.title"
            placeholder="Mailbox / notification title"
            value={values.title}
            onChange={onChange}
            error={getNestedTouched(touched, 'title') ? getNestedString(errors, 'title') : ''}
            maxLength={CONTEST_V2_MAX.title}
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={12}>
          <TextareaFieldInput
            rows={4}
            label="Message"
            name="v2.message"
            placeholder="Message body"
            value={values.message}
            onChange={onChange}
            error={getNestedTouched(touched, 'message') ? getNestedString(errors, 'message') : ''}
            maxLength={CONTEST_V2_MAX.message}
          />
        </Col>
      </Row>
    </>
  );
}
